import { useEffect, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ApiError, Campaign, Contact, api, getHost, getToken } from "../api";
import EnrolledContacts from "../components/EnrolledContacts";
// (ApiError + Contact are used inside EnrollModal below; keep imports intentional.)

type Metrics = {
  campaign_id: string;
  enrolled_total: number;
  by_state: { state: string; n: number }[];
  sent_last_24h: number;
  total_replies: number;
  classification_distribution: { label: string; n: number }[];
  rates: {
    delivery_pct: number;
    reply_pct: number;
    win_pct: number;
    bounce_pct: number;
    unsub_pct: number;
  };
};

export default function CampaignDetail() {
  const { id } = useParams<{ id: string }>();
  const [cmp, setCmp] = useState<Campaign | null>(null);
  const [metrics, setMetrics] = useState<Metrics | null>(null);
  const [err, setErr] = useState<string | null>(null);

  const [enrollOpen, setEnrollOpen] = useState(false);
  const [retemplateOpen, setRetemplateOpen] = useState(false);

  useEffect(() => {
    if (!id) return;
    api<Campaign>(`/api/campaigns/${id}`).then(setCmp).catch((e) => setErr(e.message));
    api<Metrics>(`/api/metrics/${id}`).then(setMetrics).catch(() => {});
  }, [id]);

  const setStatus = async (status: "draft" | "active" | "paused") => {
    if (!id) return;
    try {
      await api(`/api/campaigns/${id}`, { method: "PATCH", body: JSON.stringify({ status }) });
      const fresh = await api<Campaign>(`/api/campaigns/${id}`);
      setCmp(fresh);
    } catch (e) {
      alert((e as Error).message);
    }
  };

  const tick = async () => {
    try {
      await api("/api/admin/tick", { method: "POST" });
      alert("Drip tick fired. Refresh in a minute.");
    } catch (e) {
      alert((e as Error).message);
    }
  };

  if (err) return <div className="card border-red-200 bg-red-50 p-4 text-sm text-red-700">{err}</div>;
  if (!cmp) return <div className="text-sm text-ink-500">loading…</div>;

  return (
    <div className="space-y-6">
      <div>
        <Link to="/campaigns" className="text-xs text-ink-500 hover:text-ink-700">← All campaigns</Link>
        <div className="flex items-start justify-between gap-4 mt-2">
          <div>
            <h1 className="text-2xl font-semibold tracking-tight">{cmp.name}</h1>
            <div className="text-xs font-mono text-ink-500 mt-1">{cmp.id}</div>
          </div>
          <div className="flex flex-wrap gap-2">
            {cmp.status !== "active" && <button onClick={() => setStatus("active")} className="btn-secondary">Activate</button>}
            {cmp.status === "active" && <button onClick={() => setStatus("paused")} className="btn-secondary">Pause</button>}
            <button onClick={tick} className="btn-secondary">Fire drip tick</button>
            <button onClick={() => setRetemplateOpen(true)} className="btn-secondary">Regenerate copy</button>
            <button onClick={() => setEnrollOpen(true)} className="btn-primary">Enroll contacts</button>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <Stat label="Enrolled" value={metrics?.enrolled_total ?? "—"} />
        <Stat label="Sent (24h)" value={metrics?.sent_last_24h ?? "—"} />
        <Stat label="Replies" value={metrics?.total_replies ?? "—"} />
        <Stat label="Reply rate" value={metrics?.rates ? `${metrics.rates.reply_pct}%` : "—"} hint={metrics ? "of sent" : ""} />
      </div>

      {metrics && metrics.enrolled_total > 0 && (
        <div className="card p-4">
          <h2 className="text-sm font-semibold mb-3">Funnel</h2>
          <div className="grid grid-cols-2 sm:grid-cols-5 gap-3 text-sm">
            <Funnel label="Delivery" value={metrics.rates.delivery_pct} hint="enrolled → sent" />
            <Funnel label="Reply" value={metrics.rates.reply_pct} hint="sent → replied" />
            <Funnel label="Win" value={metrics.rates.win_pct} hint="replied → won" />
            <Funnel label="Bounce" value={metrics.rates.bounce_pct} hint="enrolled → bounced" tone="warn" />
            <Funnel label="Unsub" value={metrics.rates.unsub_pct} hint="enrolled → unsub" tone="warn" />
          </div>
        </div>
      )}

      {metrics && metrics.classification_distribution.length > 0 && (
        <div className="card p-5 space-y-2">
          <h2 className="text-sm font-semibold">AI classification of replies</h2>
          <div className="flex flex-wrap gap-2">
            {metrics.classification_distribution.map((c) => (
              <div key={c.label} className="rounded-md border border-ink-200 px-3 py-1.5 text-sm">
                <span className="text-ink-500 mr-2">{c.label}</span>
                <span className="font-mono">{c.n}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      <EnrolledContacts campaignId={cmp.id} />

      <div className="card p-5">
        <h2 className="text-sm font-semibold mb-3">Configuration</h2>
        <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-2 text-sm">
          <KV k="From" v={`${cmp.from_local}@${cmp.from_domain || "<env default>"}`} mono />
          <KV k="From name" v={cmp.from_name ?? ""} />
          <KV k="Reply domain" v={cmp.reply_domain || "(matches from)"} mono />
          <KV k="Hourly / daily cap" v={`${cmp.hourly_cap} / ${cmp.daily_cap}`} />
          <KV k="Send window UTC" v={`${cmp.send_window_start_hour}–${cmp.send_window_end_hour}`} />
          <KV k="Shadow mode" v={cmp.shadow_mode ? "on" : "off"} />
          <KV k="Auto-safe threshold" v={String(cmp.auto_safe_threshold ?? "—")} />
          <KV k="Notify webhook" v={cmp.notify_webhook_url || "—"} mono />
          <KV k="Success webhook" v={cmp.success_webhook_url || "—"} mono />
        </dl>
        {!cmp.mailing_address && (
          <div className="mt-3 rounded-md border border-amber-300 bg-amber-50 p-3 text-xs text-amber-900">
            ⚠ Mailing address is empty. <strong>Sends are blocked</strong> until you set one. Click the campaign config in
            the dashboard to add it (or PATCH <span className="font-mono">/api/campaigns/{cmp.id}</span> with{" "}
            <span className="font-mono">{`{ "mailing_address": "..." }`}</span>).
          </div>
        )}
        {cmp.mailing_address && (
          <div className="mt-3 rounded-md border border-ink-200 bg-ink-50 p-3 text-xs">
            <div className="label">Footer mailing address</div>
            <pre className="whitespace-pre-wrap font-sans text-ink-700">{cmp.mailing_address}</pre>
          </div>
        )}
        <div className="mt-3 rounded-md border border-ink-200 bg-ink-50 p-3 text-xs text-ink-600">
          Per-contact <span className="font-mono">custom_subject</span> / <span className="font-mono">custom_body</span>{" "}
          override these for the first send. Use{" "}
          <button onClick={() => setRetemplateOpen(true)} className="underline">Regenerate copy</button>{" "}
          to rewrite that per-contact copy in bulk from a single template.
        </div>
        <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-3 text-xs">
          <EditableBlock
            title="Subject"
            value={cmp.subject_template ?? ""}
            onSave={async (next) => {
              await api(`/api/campaigns/${cmp.id}`, { method: "PATCH", body: JSON.stringify({ subject_template: next }) });
              setCmp({ ...cmp, subject_template: next });
            }}
          />
          <EditableBlock
            title="Body template"
            value={cmp.body_template ?? ""}
            onSave={async (next) => {
              await api(`/api/campaigns/${cmp.id}`, { method: "PATCH", body: JSON.stringify({ body_template: next }) });
              setCmp({ ...cmp, body_template: next });
            }}
            textareaRows={10}
          />
          <Block title="AI persona" body={cmp.ai_persona ?? ""} />
          <Block title="AI objective" body={cmp.ai_objective ?? ""} />
          <Block title="AI script" body={cmp.ai_script ?? ""} className="sm:col-span-2" />
        </div>
      </div>

      {enrollOpen && (
        <EnrollModal
          campaignId={cmp.id}
          onClose={() => setEnrollOpen(false)}
          onChange={() => {
            // refresh metrics
            api<Metrics>(`/api/metrics/${cmp.id}`).then(setMetrics).catch(() => {});
          }}
        />
      )}
      {retemplateOpen && (
        <RetemplateModal
          campaignId={cmp.id}
          initialSubject={cmp.subject_template ?? ""}
          initialBody={cmp.body_template ?? ""}
          onClose={() => setRetemplateOpen(false)}
        />
      )}
    </div>
  );
}

function EditableBlock({
  title,
  value,
  onSave,
  className,
  textareaRows = 4,
}: {
  title: string;
  value: string;
  onSave: (next: string) => Promise<void>;
  className?: string;
  textareaRows?: number;
}) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  // Reset local draft when the source value changes (e.g., refetch after save).
  useEffect(() => { setDraft(value); }, [value]);

  const save = async () => {
    setSaving(true);
    setErr(null);
    try {
      await onSave(draft);
      setEditing(false);
    } catch (e) {
      setErr(e instanceof ApiError ? `${e.status} ${e.body.slice(0, 200)}` : (e as Error).message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className={`rounded-md border border-ink-200 p-3 ${className ?? ""}`}>
      <div className="flex items-center justify-between">
        <div className="label">{title}</div>
        {!editing ? (
          <button onClick={() => setEditing(true)} className="text-xs text-ink-500 hover:text-ink-900">Edit</button>
        ) : (
          <div className="flex gap-2">
            <button
              onClick={() => { setDraft(value); setEditing(false); setErr(null); }}
              className="text-xs text-ink-500 hover:text-ink-900"
              disabled={saving}
            >
              Cancel
            </button>
            <button onClick={save} disabled={saving} className="text-xs font-medium text-blue-700 hover:text-blue-900">
              {saving ? "saving…" : "Save"}
            </button>
          </div>
        )}
      </div>
      {!editing ? (
        <pre className="whitespace-pre-wrap font-sans text-ink-700 text-sm mt-1">{value || "—"}</pre>
      ) : (
        <textarea
          className="input mt-1 font-mono text-xs"
          rows={textareaRows}
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
        />
      )}
      {err && <div className="mt-2 text-xs text-red-700">{err}</div>}
    </div>
  );
}

type RetemplatePreview = {
  contact_id: string;
  email: string | null;
  name: string | null;
  category: string | null;
  vars: Record<string, string>;
  subject: string;
  body: string;
};

type RetemplateResp = {
  matched: number;
  dry_run?: boolean;
  samples?: RetemplatePreview[];
  updated?: number;
  scope?: string;
  category?: string | null;
};

function RetemplateModal({
  campaignId,
  initialSubject,
  initialBody,
  onClose,
}: {
  campaignId: string;
  initialSubject: string;
  initialBody: string;
  onClose: () => void;
}) {
  const [subject, setSubject] = useState(initialSubject);
  const [body, setBody] = useState(initialBody);
  const [scope, setScope] = useState<"queued" | "all">("queued");
  const [category, setCategory] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [samples, setSamples] = useState<RetemplatePreview[] | null>(null);
  const [matched, setMatched] = useState<number | null>(null);
  const [applied, setApplied] = useState<RetemplateResp | null>(null);

  const loadSample = async () => {
    setErr(null);
    try {
      // Pull a queued contact's current custom_* as a starting template.
      const r = await api<{ contacts: Array<{ contact_id: string }> }>(
        `/api/campaigns/${campaignId}/contacts?state=queued&limit=1`,
      );
      const cc = r.contacts?.[0];
      if (!cc) { setErr("No queued contact to load from."); return; }
      const cd = await api<{ custom_subject?: string; custom_body?: string }>(`/api/contacts/${cc.contact_id}`);
      if (cd.custom_subject) setSubject(cd.custom_subject);
      if (cd.custom_body) setBody(cd.custom_body);
    } catch (e) {
      setErr(e instanceof ApiError ? `${e.status} ${e.body.slice(0, 200)}` : (e as Error).message);
    }
  };

  const call = async (dryRun: boolean) => {
    setBusy(true);
    setErr(null);
    setApplied(null);
    try {
      const r = await api<RetemplateResp>(`/api/campaigns/${campaignId}/retemplate`, {
        method: "POST",
        body: JSON.stringify({
          subject_template: subject,
          body_template: body,
          scope,
          category: category.trim() || undefined,
          dry_run: dryRun,
          sample_size: 3,
        }),
      });
      setMatched(r.matched);
      if (dryRun) {
        setSamples(r.samples ?? []);
      } else {
        setSamples(null);
        setApplied(r);
      }
    } catch (e) {
      setErr(e instanceof ApiError ? `${e.status} ${e.body.slice(0, 200)}` : (e as Error).message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-20 flex items-center justify-center bg-black/40 px-4">
      <div className="card w-full max-w-4xl p-5 max-h-[90vh] flex flex-col">
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-semibold">Regenerate copy</h3>
          <button onClick={onClose} className="text-ink-500 hover:text-ink-900">✕</button>
        </div>

        <p className="text-xs text-ink-500 mb-3">
          Rewrites every matched contact's <span className="font-mono">custom_subject</span> /
          <span className="font-mono"> custom_body</span> from the templates below. Variables:{" "}
          <span className="font-mono">{`{{name}}`}</span>, <span className="font-mono">{`{{company}}`}</span>,{" "}
          <span className="font-mono">{`{{category}}`}</span>, <span className="font-mono">{`{{website}}`}</span>,{" "}
          <span className="font-mono">{`{{city}}`}</span>. Conditionals:{" "}
          <span className="font-mono">{`{{#website}}…{{/website}}`}</span>,{" "}
          <span className="font-mono">{`{{^website}}…{{/website}}`}</span>.
        </p>

        <div className="flex-1 overflow-auto space-y-3">
          <div className="flex items-center justify-between">
            <div className="label">Subject template</div>
            <button onClick={loadSample} className="text-xs text-blue-700 hover:text-blue-900">
              Load from a sample queued contact
            </button>
          </div>
          <input className="input font-mono text-xs" value={subject} onChange={(e) => setSubject(e.target.value)} />

          <div>
            <div className="label">Body template</div>
            <textarea
              className="input font-mono text-xs"
              rows={14}
              value={body}
              onChange={(e) => setBody(e.target.value)}
            />
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <div>
              <div className="label">Scope</div>
              <div className="flex gap-3 text-sm">
                <label className="flex items-center gap-1">
                  <input type="radio" checked={scope === "queued"} onChange={() => setScope("queued")} />
                  Queued only
                </label>
                <label className="flex items-center gap-1">
                  <input type="radio" checked={scope === "all"} onChange={() => setScope("all")} />
                  All in campaign
                </label>
              </div>
            </div>
            <div className="sm:col-span-2">
              <div className="label">Category filter (optional)</div>
              <input
                className="input"
                placeholder="e.g. power washing"
                value={category}
                onChange={(e) => setCategory(e.target.value)}
              />
            </div>
          </div>

          {samples && (
            <div className="space-y-3 mt-2">
              <div className="label">Preview ({matched} contacts match — showing {samples.length})</div>
              {samples.map((s) => (
                <div key={s.contact_id} className="rounded-md border border-ink-200 p-3 text-xs">
                  <div className="text-ink-500 mb-1">
                    <span className="font-mono">{s.email}</span> · {s.name ?? "—"} · {s.category ?? "—"}
                  </div>
                  <div className="font-medium text-ink-800">Subject: {s.subject}</div>
                  <pre className="whitespace-pre-wrap font-sans text-ink-700 text-sm mt-2">{s.body}</pre>
                </div>
              ))}
            </div>
          )}

          {applied && (
            <div className="rounded-md border border-emerald-300 bg-emerald-50 p-3 text-sm text-emerald-900">
              Rewrote <strong>{applied.updated}</strong> of {applied.matched} matched contacts
              ({applied.scope}{applied.category ? `, category=${applied.category}` : ""}).
            </div>
          )}

          {err && <div className="rounded-md border border-red-300 bg-red-50 p-3 text-sm text-red-700">{err}</div>}
        </div>

        <div className="flex justify-end gap-2 mt-4 pt-3 border-t border-ink-200">
          <button onClick={onClose} className="btn-secondary" disabled={busy}>Close</button>
          <button onClick={() => call(true)} disabled={busy || !subject || !body} className="btn-secondary">
            {busy ? "…" : "Preview"}
          </button>
          <button
            onClick={() => {
              if (matched == null) { setErr("Run Preview first so you see how many contacts will change."); return; }
              if (!confirm(`Rewrite custom_subject/custom_body on ${matched} contacts? This cannot be undone.`)) return;
              void call(false);
            }}
            disabled={busy || !subject || !body}
            className="btn-primary"
          >
            {busy ? "applying…" : "Apply"}
          </button>
        </div>
      </div>
    </div>
  );
}

function EnrollModal({
  campaignId,
  onClose,
  onChange,
}: {
  campaignId: string;
  onClose: () => void;
  onChange: () => void;
}) {
  const [tab, setTab] = useState<"pick" | "csv">("pick");

  // Pick-from-contacts state
  const [contacts, setContacts] = useState<Contact[]>([]);
  const [search, setSearch] = useState("");
  const [picked, setPicked] = useState<Set<string>>(new Set());
  const [enrolling, setEnrolling] = useState(false);
  const [pickResult, setPickResult] = useState<string | null>(null);

  // CSV state
  const fileRef = useRef<HTMLInputElement>(null);
  const [csvBusy, setCsvBusy] = useState(false);
  const [csvResult, setCsvResult] = useState<string | null>(null);

  useEffect(() => {
    if (tab !== "pick") return;
    api<{ contacts: Contact[] }>(`/api/contacts?limit=100${search ? `&q=${encodeURIComponent(search)}` : ""}`)
      .then((r) => setContacts(r.contacts))
      .catch(() => {});
  }, [tab, search]);

  const enrollPicked = async () => {
    if (picked.size === 0) return;
    setEnrolling(true);
    setPickResult(null);
    try {
      const r = await api<{ enrolled: number; skipped_dup: number; requested: number }>(
        `/api/campaigns/${campaignId}/enroll`,
        { method: "POST", body: JSON.stringify({ contact_ids: [...picked], schedule_now: true }) },
      );
      setPickResult(`Enrolled ${r.enrolled}, skipped duplicates ${r.skipped_dup}.`);
      setPicked(new Set());
      onChange();
    } catch (e) {
      setPickResult(e instanceof ApiError ? `${e.status} ${e.body.slice(0, 200)}` : (e as Error).message);
    } finally {
      setEnrolling(false);
    }
  };

  const uploadCsv = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setCsvBusy(true);
    setCsvResult(null);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("source_label", file.name);
      const r = await fetch(`${getHost()}/api/campaigns/${campaignId}/import-and-enroll`, {
        method: "POST",
        headers: { Authorization: `Bearer ${getToken()}` },
        body: fd,
      });
      const text = await r.text();
      if (!r.ok) throw new ApiError(r.status, text);
      const j = JSON.parse(text);
      setCsvResult(
        `From ${file.name}: imported ${j.csv.inserted} new contacts (${j.csv.skipped_dup} duplicates, ${j.csv.skipped_no_email} skipped). Enrolled ${j.enrolled} into this campaign (${j.skipped_already_enrolled} already enrolled).`,
      );
      onChange();
    } catch (e) {
      setCsvResult(e instanceof ApiError ? `${e.status} ${e.body.slice(0, 200)}` : (e as Error).message);
    } finally {
      setCsvBusy(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  return (
    <div className="fixed inset-0 z-20 flex items-center justify-center bg-black/40 px-4">
      <div className="card w-full max-w-2xl p-5 max-h-[85vh] flex flex-col">
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-semibold">Add contacts to this campaign</h3>
          <button onClick={onClose} className="text-ink-500 hover:text-ink-900">✕</button>
        </div>

        <div className="flex border-b border-ink-200 mb-4 -mx-1">
          {(
            [
              { k: "pick", label: "Pick from contacts" },
              { k: "csv", label: "Upload CSV" },
            ] as const
          ).map((t) => (
            <button
              key={t.k}
              onClick={() => setTab(t.k)}
              className={`px-3 py-2 text-sm border-b-2 -mb-px ${
                tab === t.k ? "border-ink-900 text-ink-900 font-medium" : "border-transparent text-ink-500 hover:text-ink-700"
              }`}
            >
              {t.label}
            </button>
          ))}
        </div>

        {tab === "pick" ? (
          <>
            <input
              className="input mb-3"
              placeholder="Search email, name, category"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
            <div className="flex-1 overflow-auto rounded-md border border-ink-200">
              {contacts.length === 0 ? (
                <div className="p-6 text-center text-sm text-ink-500">No contacts. Upload a CSV first.</div>
              ) : (
                <ul className="divide-y divide-ink-100">
                  {contacts.map((c) => (
                    <li key={c.id} className="flex items-center gap-3 px-3 py-2">
                      <input
                        type="checkbox"
                        checked={picked.has(c.id)}
                        onChange={(e) => {
                          const next = new Set(picked);
                          if (e.target.checked) next.add(c.id); else next.delete(c.id);
                          setPicked(next);
                        }}
                      />
                      <div className="flex-1 min-w-0">
                        <div className="text-sm truncate font-mono">{c.email}</div>
                        <div className="text-xs text-ink-500 truncate">{c.name ?? ""} {c.category ? `· ${c.category}` : ""}</div>
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </div>
            <div className="flex items-center justify-between mt-3">
              <div className="text-sm text-ink-500">{picked.size} selected</div>
              <div className="flex gap-2">
                <button className="btn-secondary" onClick={onClose}>Close</button>
                <button className="btn-primary" disabled={enrolling || picked.size === 0} onClick={enrollPicked}>
                  {enrolling ? "enrolling…" : `Enroll ${picked.size}`}
                </button>
              </div>
            </div>
            {pickResult && <div className="mt-3 text-xs text-ink-700 font-mono">{pickResult}</div>}
          </>
        ) : (
          <>
            <div className="rounded-md border border-dashed border-ink-300 bg-ink-50 p-8 text-center">
              <p className="text-sm text-ink-700 mb-1">Upload a CSV to import contacts and enroll them in this campaign in one step.</p>
              <p className="text-xs text-ink-500 mb-4">
                Supports the FB scraper output format (must have an <span className="font-mono">email</span> column).
                Existing contacts (matched by email) are reused, not duplicated.
              </p>
              <label className={`btn-primary cursor-pointer ${csvBusy ? "opacity-50 pointer-events-none" : ""}`}>
                {csvBusy ? "uploading…" : "Choose CSV…"}
                <input ref={fileRef} type="file" accept=".csv,text/csv" className="hidden" onChange={uploadCsv} />
              </label>
            </div>
            {csvResult && (
              <div className="mt-3 rounded-md border border-ink-200 bg-ink-100 p-3 text-xs font-mono text-ink-700 whitespace-pre-wrap">
                {csvResult}
              </div>
            )}
            <div className="flex justify-end mt-3">
              <button className="btn-secondary" onClick={onClose}>Done</button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function Stat({ label, value, hint }: { label: string; value: React.ReactNode; hint?: string }) {
  return (
    <div className="card p-4">
      <div className="label">{label}</div>
      <div className="text-2xl font-semibold tracking-tight mt-1 capitalize">{value}</div>
      {hint && <div className="text-xs text-ink-400 mt-1">{hint}</div>}
    </div>
  );
}

function Funnel({ label, value, hint, tone }: { label: string; value: number; hint?: string; tone?: "warn" }) {
  const color = tone === "warn" ? "text-amber-700" : value > 0 ? "text-ink-900" : "text-ink-400";
  return (
    <div>
      <div className="label">{label}</div>
      <div className={`text-xl font-semibold ${color}`}>{value}%</div>
      {hint && <div className="text-xs text-ink-400 mt-0.5">{hint}</div>}
    </div>
  );
}
function KV({ k, v, mono }: { k: string; v: string; mono?: boolean }) {
  return (
    <div className="flex justify-between gap-3 border-b border-ink-100 py-1">
      <dt className="text-ink-500">{k}</dt>
      <dd className={`text-right truncate ${mono ? "font-mono text-xs" : ""}`}>{v}</dd>
    </div>
  );
}
function Block({ title, body, className }: { title: string; body: string; className?: string }) {
  return (
    <div className={`rounded-md border border-ink-200 p-3 ${className ?? ""}`}>
      <div className="label">{title}</div>
      <pre className="whitespace-pre-wrap font-sans text-ink-700 text-sm">{body || "—"}</pre>
    </div>
  );
}
